import React from "react";
import { format } from "date-fns";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import { Link } from "react-router-dom";
import { useI18n } from "../../../i18n";

import { css } from "@emotion/css";
import { desktop, laptop, tablet } from "../../../styles/breakpoints";
import buttons from "../../../styles/buttons";

const escapeHtml = (str) => {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

const Body = ({ post, posts }) => {
  const { t } = useI18n();
  const options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: ({
        data: {
          target: { fields },
        },
      }) =>
        `<img src="${escapeHtml(fields.file.url)}" height="${fields.file.details.image.height}" width="${fields.file.details.image.width}" alt="${escapeHtml(fields.description)}"/>`,
    },
  };

  return (
    <div className={style.container}>
      <div className="sidebar">
        <h3>{t("post_recent")}</h3>
        <div className="posts">
          {Object.values(posts).map((v, i) => {
            if (i < 8 && post.fields.slug !== v.fields.slug) {
              return (
                <Link key={i} className="post" to={`/post/${v.fields.slug}`}>
                  <img
                    src={v.fields.image.fields.file.url}
                    alt={v.fields.title}
                  />
                  <div className="content">
                    <h5>{v.fields.title}</h5>
                    <div>
                      <span>{v.fields.category}</span>{" "}
                      <span>
                        {format(new Date(v.fields.date), "LLLL dd, yyyy")}
                      </span>
                    </div>
                    <p>{v.description}</p>
                  </div>
                </Link>
              );
            }
            return <></>;
          })}
        </div>
      </div>
      <div className="body">
        <div className="meta">
          {post?.fields?.minutesToRead || "5"} {t("post_min")} &#x2022;{" "}
          {format(new Date(post?.fields?.date), "LLLL dd, yyyy")}
        </div>
        <h3>{post.fields.title}</h3>
        {post?.fields?.content && (
          <div className="content">
            {parse(DOMPurify.sanitize(documentToHtmlString(post?.fields?.content, options)))}
          </div>
        )}
          <Link
            className={`${buttons.primary} ${buttons.inter}`}
            style={{ margin: "auto", marginTop: -30, marginBottom: 30 }}
            to={"/subscribe"}
          >
            {t("post_join_waitlist")}
          </Link>
      </div>
    </div>
  );
};

const style = {
  container: css({
    marginTop: 110,
    paddingBottom: 80,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    alignContent: "center",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: 60,
    paddingLeft: 40,
    paddingRight: 40,
    backgroundColor: "#fff",
    maxWidth: "1276px",
    marginLeft: "auto",
    marginRight: "auto",

    ".body": {
      display: "flex",
      flexDirection: "column",
      alignContent: "flex-start",
      flexWrap: "nowrap",
      alignItems: "flex-start",
      justifyContent: "flex-start",

      span: {
        color: "#FF6AFF",
      },

      "h1,h2,h3": {
        color: "var(--Neutral-100, #121212)",
        fontFamily: "Syne",
        fontSize: 48,
        fontStyle: "normal",
        fontWeight: 600,
        lineHeight: "64px",
        letterSpacing: "-0.48px",
      },
      ".meta": {
        color: "#717171",
        fontFamily: "Inter",
        fontSize: 20,
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "30px",
        letterSpacing: 0.6,
        marginBottom: 20,
      },
      ".content": {
        color: "#51555C",
        fontFamily: "Inter",
        fontSize: 16,
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "24px",
        marginTop: 40,

        h1: {
          color: "#121212",
          fontFamily: "Syne",
          fontSize: 48,
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "64px",
          letterSpacing: "-0.48px",
          marginBottom: 40,
        },
        "h2,h3,h4,h5": {
          color: "#21243D",
          fontFamily: "Syne",
          fontSize: 25.2,
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "31.5px",
          marginBottom: 24,
        },

        p: {
          color: "#51555C",
          fontFamily: "Inter",
          fontSize: 16,
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: "24px",
          marginBottom: 48,
        },

        li: {
          p: {
            marginBottom: 18,
          },
        },

        b: {
          //color: "#FF6AFF",
        },
        a: {
          color: "#FF6AFF",
          fontFamily: "Inter",
          fontSize: 16,
          fontStyle: "italic",
          fontWeight: 500,
          lineHeight: "24px",
          textDecorationLine: "underline",
        },

        img: {
          borderRadius: 30,
          marginTop: 35,
          marginBottom: 50,
          width: "100%",
          height: "auto",
        },
      },
    },
    ".sidebar": {
      display: "flex",
      flexWrap: "wrap",
      flexDirection: "column",
      alignContent: "center",
      justifyContent: "center",
      alignItems: "flex-start",
      gap: 32,
      marginBottom: 80,
      maxWidth: 1190,

      ".posts": {
        display: "flex",
        flexWrap: "wrap",
        flexDirection: "column",
        alignContent: "center",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: 32,
      },

      h3: {
        color: "#121212",
        fontFamily: "Syne",
        fontSize: 28,
        fontStyle: "normal",
        fontWeight: 600,
        lineHeight: "32px",
        marginTop: 50,
        marginBottom: 15,
      },
      ".post": {
        width: 361,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: 50,
        border: "1px solid rgba(255, 174, 105, 0.20)",
        background: "#FFF8F2",
        overflow: "hidden",

        img: {
          width: "100%",
          height: "auto",
        },

        ".content": {
          marginTop: 0,
          padding: 24,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: 16,
        },
        h5: {
          color: "#21243D",
          fontFamily: "Syne",
          fontSize: 26,
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "31.5px",
        },
        p: {
          color: "#21243D",
          fontFamily: "Inter",
          fontSize: 14,
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "normal",
        },
        div: {
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: 20,

          span: {
            color: "#FFF",
            textAlign: "center",
            fontFamily: "Syne",
            fontSize: 16.8,
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "normal",
            borderRadius: 100,
            background: "#D7B7FD",
            padding: "8px 16px",
          },
        },
      },
    },
    [desktop]: {
      flexDirection: "column-reverse",
      marginTop: 180,

      ".post": {
        "h1,h2,h3": {
          fontSize: 52,
          lineHeight: "60px",
          letterSpacing: "-0.52px",
        },
        ".meta": {
          fontSize: 16,
          lineHeight: "24px",
          letterSpacing: 0.6,
          marginBottom: 20,
        },
        ".content": {
          marginTop: 42,

          "h4,h5": {
            fontSize: 32,
            lineHeight: "36px",
          },

          img: {
            marginTop: 80,
            marginBottom: 60,
          },
        },
      },
      ".sidebar": {
        alignItems: "center",
        marginLeft: "auto",
        marginRight: "auto",

        h3: {
          fontSize: 52,
          lineHeight: "50px",
          marginTop: 40,
          marginBottom: 20,
        },
        ".posts": {
          flexDirection: "row",
          gap: 32,
          maxWidth: 700,
        },
        ".post": {
          width: 327,
          borderRadius: 50,

          h5: {
            fontSize: 26,
            lineHeight: "28px",
          },
          p: {
            fontSize: 16,
          },
          div: {
            gap: 20,

            span: {
              fontSize: 16,
            },
          },
        },
      },
    },
    [laptop]: {
      marginTop: 160,
      paddingLeft: 30,
      paddingRight: 30,

      ".post": {
        "h1,h2,h3": {
          fontSize: 48,
          lineHeight: "60px",
          letterSpacing: "-0.48px",
        },
        ".meta": {
          fontSize: 20,
          lineHeight: "24px",
          letterSpacing: 0.6,
          marginBottom: 20,
        },
        ".content": {
          marginTop: 42,
          fontSize: 14,

          "h4,h5": {
            fontSize: 28,
            lineHeight: "30px",
          },
          p: {
            fontSize: 14,
          },
          img: {
            marginTop: 80,
            marginBottom: 60,
          },
        },
      },
      ".sidebar": {
        h3: {
          fontSize: 46,
          lineHeight: "50px",
          marginTop: 60,
          marginBottom: 20,
        },
        ".post": {
          width: 300,
          borderRadius: 50,
          gap: 16,

          h5: {
            fontSize: 24,
            lineHeight: "26px",
          },
          p: {
            fontSize: 14,
          },
          div: {
            gap: 8,

            span: {
              fontSize: 14,
            },
          },
        },
      },
    },
    [tablet]: {
      marginTop: 100,
      paddingLeft: 19,
      paddingRight: 19,
      h2: {
        fontSize: 34,
        lineHeight: "47px",
        letterSpacing: "-0.34px",
        marginBottom: 42,
      },

      ".post": {
        "h1,h2,h3": {
          fontSize: 42,
          lineHeight: "52px",
          letterSpacing: "-0.42px",
        },
        ".content": {
          marginTop: 42,
          fontSize: 14,

          "h4,h5": {
            fontSize: 24,
            lineHeight: "38px",
          },
          p: {
            fontSize: 14,
          },
          img: {
            marginTop: 80,
            marginBottom: 60,
          },
        },
      },
      ".sidebar": {
        h3: {
          fontSize: 40,
          lineHeight: "48px",
          marginTop: 40,
          marginBottom: 10,
        },
        ".post": {
          maxWidth: 300,
          width: "100% !important",
          borderRadius: 36,
          gap: 13,
          paddingBottom: 10,

          h5: {
            fontSize: 18,
            lineHeight: "23px",
          },
          p: {
            fontSize: 12,
          },
          div: {
            gap: 8,

            span: {
              fontSize: 12,
            },
          },
        },
      },
    },
  }),
};

export default Body;
